/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { AxisPosition } from '#utils/UNSAFE_visTypes/chart';
import { cloneDimension } from '#utils/PRIVATE_visLayoutUtils';
import { TextDimensions, GetTextDimensionsType } from '#hooks/PRIVATE_useTextDimensions';
import { endsWithEllipsis, truncateMultilineText, truncateSingleLineText } from './truncation';
import { Dimension } from '#utils/UNSAFE_visTypes/common';

const REPRESENTATIVE_TEXT = 'MV';

/**
 * Returns the (x, y) coordinate of the text given the width and height of the chart.
 * @param position
 * @param availSpace
 * @param textDims
 * @returns
 */
export const positionMultilineText = (
  lines: string[],
  dims: Dimension[],
  lineHeight: number,
  position: AxisPosition,
  availSpace: Dimension,
  isRtl: boolean
) => {
  const space = cloneDimension(availSpace);
  return lines.map((_, i) => {
    const textCoord = centerTextInAvailSpace(position, space, isRtl, dims[i]);

    if (position === 'top') {
      space.y += lineHeight;
      space.height -= lineHeight;
    } else if (position === 'left') {
      space.x += lineHeight;
      space.width -= lineHeight;
    } else if (position === 'bottom') {
      space.height -= lineHeight;
    } else {
      space.width -= lineHeight;
    }

    return textCoord;
  });
};

/**
 * Returns the x and y positon for a centered text.
 * @param position
 * @param availSpace
 * @param isRtl
 * @param textDims
 * @returns
 */
export function centerTextInAvailSpace(
  position: AxisPosition,
  availSpace: Dimension,
  isRtl: boolean,
  textDims: { width: number; height: number }
) {
  let x: number;
  let y: number;

  if (position === 'left') {
    x = availSpace.x + textDims.height / 2 - (isRtl ? textDims.height / 2 : 0);
    y = availSpace.y + availSpace.height / 2;
  } else if (position === 'right') {
    x = availSpace.x + availSpace.width - (isRtl ? textDims.height / 2 : 0);
    y = availSpace.y + availSpace.height / 2;
  } else if (position === 'top') {
    x = availSpace.x + availSpace.width / 2;
    y = availSpace.y + textDims.height / 2;
  } else {
    x = availSpace.x + availSpace.width / 2;
    y = availSpace.y + availSpace.height - textDims.height / 2;
  }

  return { x, y };
}

/**
 * Returns the props for the text component.
 */
export function getTextProps(
  lines: string[],
  textStyle: Record<string, any>,
  bounds: TextDimensions,
  isRotated: boolean,
  isRtl: boolean,
  getTextDimension: GetTextDimensionsType
) {
  let { x, y } = bounds;

  const lineHeight = getTextDimension(REPRESENTATIVE_TEXT, textStyle).width;

  const props: any = [];

  lines.forEach((line, i) => {
    const transform = isRotated
      ? `matrix(0, ${isRtl ? 1 : -1}, ${isRtl ? -1 : 1}, 0, ${x}, ${y})`
      : undefined;

    props.push({
      text: line,
      x: isRotated ? undefined : x,
      y: isRotated ? undefined : y + i * lineHeight,
      transform: isRotated ? transform : undefined
    });

    x += isRotated ? lineHeight : 0;
    y += isRotated ? 0 : lineHeight;
  });
  return props;
}

/**
 * Returns the text info for the given text.
 */
export function getTextInfo(
  text: string,
  textStyle: Record<string, string | number>,
  width: number,
  height: number,
  isMultiline: boolean,
  minChar: number,
  getTextDimension: GetTextDimensionsType
) {
  const lines = isMultiline
    ? truncateMultilineText(text, textStyle, width, height, minChar, getTextDimension)
    : [truncateSingleLineText(text, textStyle, width, height, minChar, getTextDimension)];

  const lineHeight = getTextDimension(REPRESENTATIVE_TEXT, textStyle).height;
  const dims = lines.map((line) => getTextDimension(line, textStyle));

  const didNotFit = lines.every((line) => line === '');

  const maxWidth = Math.max(
    ...dims.map((dim) => {
      return dim.width;
    })
  );

  return {
    lines,
    dims,
    didNotFit,
    height: lines.length * lineHeight,
    width: maxWidth,
    isMultiline: lines.length > 1,
    lineHeight,
    isTruncated: endsWithEllipsis(lines)
  };
}
